import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useRequestTierUpgrade, useTierUpgradeRequest } from '@/hooks/useTier';

const TIER_OPTIONS = [
  { value: 'silver', label: 'Prata', description: 'Market Intelligence básico, surface B2C Quote' },
  { value: 'gold', label: 'Ouro', description: 'Market Intelligence completo, Oportunidades, PDF Export, App B2C' },
  { value: 'lead_qual', label: 'Lead Qual', description: 'Todas as features + cobrança por lead qualificado' },
] as const;

interface TierUpgradeModalProps {
  currentTier: string;
  onClose: () => void;
}

const TIER_ORDER: Record<string, number> = { bronze: 0, silver: 1, gold: 2, lead_qual: 3 };

export function TierUpgradeModal({ currentTier, onClose }: TierUpgradeModalProps) {
  const currentOrdinal = TIER_ORDER[currentTier] ?? 0;
  const availableTiers = TIER_OPTIONS.filter((t) => (TIER_ORDER[t.value] ?? 0) > currentOrdinal);

  const [selected, setSelected] = useState<string>(availableTiers[0]?.value ?? '');
  const [reason, setReason] = useState('');

  const { data: existingReq, isError: requestError } = useTierUpgradeRequest();
  const mutation = useRequestTierUpgrade();

  const hasPending = existingReq?.request?.status === 'pending';
  // Falhando a busca, `hasPending` vira false e o formulario reaparece para
  // quem ja pediu — o parceiro manda o segundo pedido sem saber do primeiro.
  const pendingUnknown = requestError && !existingReq;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || reason.length < 10) return;
    await mutation.mutateAsync({ requestedTier: selected, reason });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Solicitar Upgrade de Plano</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded-md p-1.5 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        {pendingUnknown ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/40 p-4">
              <p className="text-sm font-medium">Não foi possível verificar suas solicitações</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Se você já pediu um upgrade, um novo pedido ficaria duplicado.
                Recarregue a página antes de enviar.
              </p>
            </div>
          </div>
        ) : hasPending ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Solicitação pendente
              </p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Você já tem uma solicitação de upgrade para{' '}
                <span className="font-semibold capitalize">{existingReq?.request?.requestedTier}</span>{' '}
                aguardando análise.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-lg border border-border py-2.5 text-sm font-medium transition-colors hover:bg-muted"
            >
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Plano desejado</label>
              <div className="space-y-2">
                {availableTiers.map((tier) => (
                  <label
                    key={tier.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      selected === tier.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={tier.value}
                      checked={selected === tier.value}
                      onChange={(e) => setSelected(e.target.value)}
                      className="mt-0.5 h-4 w-4 accent-primary"
                    />
                    <div>
                      <div className="text-sm font-semibold">{tier.label}</div>
                      <div className="text-xs text-muted-foreground">{tier.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">Justificativa</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Explique por que deseja o upgrade (min. 10 caracteres)"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {reason.length > 0 && reason.length < 10 && (
                <p className="mt-1 text-xs text-destructive">Mínimo 10 caracteres</p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium transition-colors hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!selected || reason.length < 10 || mutation.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {mutation.isPending && <Loader2 size={14} className="animate-spin" />}
                Enviar solicitação
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
